import React from 'react';
import {
  Pagination as PaginationWrapper,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

interface PaginationProps {
    totalPage: number;
    currentPage: number;
    onPageChange: (page: number) => void;
    siblingCount?: number;
}

const DOTS = 'dots';

const range = (start: number, end: number) =>
    Array.from({ length: Math.max(end - start + 1, 0) }, (_, i) => start + i);

/**
 * Builds the visible page range: always the first and last page, the current
 * page with `siblingCount` neighbours on each side, and ellipses for the gaps.
 */
const getPageRange = (totalPage: number, currentPage: number, siblingCount: number) => {
    // first + last + current + siblings on both sides + 2 ellipsis slots
    const maxSlots = siblingCount * 2 + 5;
    if (totalPage <= maxSlots) return range(1, totalPage);

    const leftSibling = Math.max(currentPage - siblingCount, 1);
    const rightSibling = Math.min(currentPage + siblingCount, totalPage);

    const showLeftDots = leftSibling > 2;
    const showRightDots = rightSibling < totalPage - 1;

    if (!showLeftDots && showRightDots) {
        return [...range(1, 3 + siblingCount * 2), DOTS, totalPage];
    }

    if (showLeftDots && !showRightDots) {
        return [1, DOTS, ...range(totalPage - (2 + siblingCount * 2), totalPage)];
    }

    return [1, DOTS, ...range(leftSibling, rightSibling), DOTS, totalPage];
};

const Pagination: React.FC<PaginationProps> = ({
    totalPage,
    currentPage,
    onPageChange,
    siblingCount = 1,
}) => {
    const total = Number.isFinite(totalPage) ? Math.max(Math.trunc(totalPage), 0) : 0;
    const current = Math.min(Math.max(currentPage, 1), Math.max(total, 1));

    const handlePageClick = (page: number) => {
        if (page < 1 || page > total || page === current) return;
        onPageChange(page);
    };

    if (total < 1) return null;

    const pages = getPageRange(total, current, siblingCount);

    return (
        <PaginationWrapper className="mt-5 flex justify-end">
            <PaginationContent className="flex-wrap">
                <PaginationItem>
                    <PaginationPrevious
                    aria-disabled={current === 1}
                    className={`!text-gray-400 ${current === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
                    onClick={() => handlePageClick(current - 1)}/>
                </PaginationItem>
                {
                    pages.map((page, i) => {
                        if (page === DOTS) {
                            return (
                                <PaginationItem key={`dots-${i}`}>
                                    <PaginationEllipsis className="!text-gray-400" />
                                </PaginationItem>
                            );
                        }

                        const pageNum = page as number;
                        const isActive = current === pageNum;

                        return (
                            <PaginationItem key={pageNum}>
                                <PaginationLink
                                isActive={isActive}
                                className={`cursor-pointer px-3 py-1 rounded-md ${
                                    isActive ? 'bg-black !text-white font-bold' : '!text-gray-400'
                                }`}
                                onClick={() => handlePageClick(pageNum)}>
                                    {pageNum}
                                </PaginationLink>
                            </PaginationItem>
                        );
                    })
                }
                <PaginationItem>
                    <PaginationNext
                    aria-disabled={current === total}
                    className={`!text-gray-400 ${current === total ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
                    onClick={() => handlePageClick(current + 1)}/>
                </PaginationItem>
            </PaginationContent>
        </PaginationWrapper>
    );
};

export default Pagination;
